import { ChangeEvent, Component, KeyboardEvent } from "react";
import clsx from "clsx";
import { ReactCodeInputProps } from "@/shared/ui/codeInput/CodeInput.d";
import { getInputArrayFromProps, getValueFromProps, uuidv4 } from "@/shared/ui/codeInput/utils.js";

const BACKSPACE_KEY = 8;
const LEFT_ARROW_KEY = 37;
const RIGHT_ARROW_KEY = 39;

const defaultInputStyle: React.CSSProperties = {
  fontFamily: "monospace",
  MozAppearance: "textfield",
  borderRadius: "6px",
  border: "1px solid",
  boxShadow: "0px 0px 10px 0px rgba(0,0,0,.10)",
  margin: "4px",
  paddingLeft: "8px",
  paddingRight: 0,
  width: "42px",
  height: "42px",
  fontSize: "32px",
  boxSizing: "border-box",
};

type State = {
  input: string[];
  value: string;
};

class ReactCodeInput extends Component<ReactCodeInputProps, State> {
  static defaultProps: Partial<ReactCodeInputProps> = {
    autoComplete: "off",
    autoFocus: true,
    isValid: true,
    disabled: false,
    forceUppercase: false,
    fields: 4,
    value: "",
    type: "text",
    filterKeyCodes: [189, 190],
    filterChars: ["-", "."],
    filterCharsIsWhitelist: false,
  };

  textInput: HTMLInputElement[] = [];

  uuid: string = uuidv4();

  constructor(props: ReactCodeInputProps) {
    super(props);

    this.state = {
      input: getInputArrayFromProps(props),
      value: getValueFromProps(props),
    };
  }

  componentDidUpdate(prevProps: ReactCodeInputProps) {
    if (prevProps.value !== this.props.value) {
      this.setState({
        input: getInputArrayFromProps(this.props),
        value: getValueFromProps(this.props),
      });
    }
  }

  handleBlur = (e: ChangeEvent<HTMLInputElement>) => {
    this.handleTouch(e.target.value);
  };

  handleTouch = (value: string) => {
    const { touch, untouch, name } = this.props;

    if (touch && untouch) {
      if (value === "") {
        touch(name);
      } else {
        untouch(name);
      }
    }
  };

  handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { filterChars, filterCharsIsWhitelist, forceUppercase, fields, type } = this.props;
    let value = String(e.target.value);

    if (forceUppercase) {
      value = value.toUpperCase();
    }

    if (type === "number") {
      value = value.replace(/[^\d]/g, "");
    }

    value = value
      .split("")
      .filter((currChar) => {
        if (filterCharsIsWhitelist) {
          return filterChars?.includes(currChar);
        }
        return !filterChars?.includes(currChar);
      })
      .join("");

    let fullValue = value;

    if (value !== "") {
      const input = [...this.state.input];
      const targetIndex = Number(e.target.dataset.id);

      if (value.length > 1) {
        value.split("").forEach((char, i) => {
          if (targetIndex + i < (fields || 0)) {
            input[targetIndex + i] = char;
          }
        });
      } else {
        input[targetIndex] = value;
      }

      input.forEach((s, i) => {
        if (this.textInput[i]) {
          this.textInput[i].value = s;
        }
      });

      const newTargetIndex = Math.min(value.length + targetIndex, (fields || 0) - 1);
      const newTarget = this.textInput[newTargetIndex];

      if (newTarget) {
        newTarget.focus();
        newTarget.select();
      }

      fullValue = input.join("");

      this.setState({ value: fullValue, input });
    }

    if (this.props.onChange && fullValue) {
      this.props.onChange(fullValue);
    }

    this.handleTouch(fullValue);
  };

  handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const target = Number(e.currentTarget.dataset.id);
    const nextTarget = this.textInput[target + 1];
    const prevTarget = this.textInput[target - 1];

    const input = [...this.state.input];
    let { value } = this.state;

    if (this.props.filterKeyCodes?.includes(e.keyCode)) {
      e.preventDefault();
      return;
    }

    switch (e.keyCode) {
      case BACKSPACE_KEY:
        e.preventDefault();
        this.textInput[target].value = "";
        input[target] = "";
        value = input.join("");
        this.setState({ value, input });
        if (!this.textInput[target].value && prevTarget) {
          prevTarget.focus();
          prevTarget.select();
        }
        if (this.props.onChange) {
          this.props.onChange(value);
        }
        break;
      case LEFT_ARROW_KEY:
        e.preventDefault();
        if (prevTarget) {
          prevTarget.focus();
          prevTarget.select();
        }
        break;
      case RIGHT_ARROW_KEY:
        e.preventDefault();
        if (nextTarget) {
          nextTarget.focus();
          nextTarget.select();
        }
        break;
      default:
        break;
    }

    this.handleTouch(value);
  };

  render() {
    const {
      className,
      disabled,
      style = {},
      inputStyle = {},
      inputStyleInvalid = {},
      isValid,
      type,
      autoFocus,
      autoComplete,
      pattern,
      inputMode,
      placeholder,
    } = this.props;

    const { input } = this.state;

    const styles = {
      container: { display: "inline-block", ...style },
      input: isValid ? inputStyle : inputStyleInvalid,
    };

    if (!className && Object.keys(inputStyle).length === 0) {
      Object.assign(inputStyle, {
        ...defaultInputStyle,
        color: "black",
        backgroundColor: "white",
        borderColor: "lightgrey",
      });
    }

    if (!className && Object.keys(inputStyleInvalid).length === 0) {
      Object.assign(inputStyleInvalid, {
        ...defaultInputStyle,
        color: "#b94a48",
        backgroundColor: "#f2dede",
        borderColor: "#eed3d7",
      });
    }

    if (disabled) {
      Object.assign(styles.input, {
        cursor: "not-allowed",
        color: "lightgrey",
        borderColor: "lightgrey",
        backgroundColor: "#efeff1",
      });
    }

    return (
      <div className={clsx(className, "react-code-input")} style={styles.container}>
        {input.map((value, i) => (
          <input
            ref={(ref) => {
              if (ref) this.textInput[i] = ref;
            }}
            id={`${this.uuid}-${i}`}
            data-id={i}
            autoFocus={autoFocus && i === 0 ? true : undefined}
            value={value}
            key={`input_${i}`}
            type={type === "number" ? "text" : type}
            min={0}
            max={9}
            style={styles.input}
            autoComplete={autoComplete}
            onFocus={(e) => e.target.select()}
            onBlur={this.handleBlur}
            onChange={this.handleChange}
            onKeyDown={this.handleKeyDown}
            disabled={disabled}
            data-valid={isValid}
            pattern={pattern}
            inputMode={inputMode}
            placeholder={placeholder}
            className="selection:bg-transparent"
          />
        ))}
      </div>
    );
  }
}

export default ReactCodeInput;
